import { DcfSdkEnumsObj } from "../../enums/DcfSdkEnum";
import { SrciEventTypeEnumsObj } from "../../enums/SrciEventTypeEnum";
import { MerchantRecognitionTokenCookieNames } from "./MerchantRecognitionTokenCookies";
var MAX_COOKIE_LIFETIME_MS = 180 * 24 * 60 * 60 * 1000;
var COOKIE_ATTRIBUTES = "Path=/; Secure; SameSite=Strict";
var DELETE_EXPIRY = "Thu, 01 Jan 1970 00:00:00 GMT";
var JWT_PART_PATTERN = /^[A-Za-z0-9_-]+$/;
var decodeJwtPart = function (part) {
    if (!JWT_PART_PATTERN.test(part) || part.length % 4 === 1) {
        throw new Error("Invalid JWT part");
    }
    var padding = "=".repeat((4 - (part.length % 4)) % 4);
    var paddedPart = "".concat(part.replace(/-/g, "+").replace(/_/g, "/")).concat(padding);
    var binaryValue = atob(paddedPart);
    var encodedBytes = Array.from(binaryValue)
        .map(function (character) {
        var hexValue = character.charCodeAt(0).toString(16);
        return "%".concat(hexValue.length === 1 ? "0".concat(hexValue) : hexValue);
    })
        .join("");
    return JSON.parse(decodeURIComponent(encodedBytes));
};
var getExpiration = function (token, now) {
    var parts = token.split(".");
    if (parts.length !== 3 || !JWT_PART_PATTERN.test(parts[2])) {
        return undefined;
    }
    try {
        var header = decodeJwtPart(parts[0]);
        var payload = decodeJwtPart(parts[1]);
        if (!header ||
            typeof header !== "object" ||
            Array.isArray(header) ||
            !payload ||
            typeof payload !== "object" ||
            Array.isArray(payload)) {
            return undefined;
        }
        var exp = payload.exp;
        if (typeof exp !== "number" || !Number.isFinite(exp)) {
            return undefined;
        }
        var expiration = exp * 1000;
        return expiration > now ? expiration : undefined;
    }
    catch (_a) {
        return undefined;
    }
};
/** @internal */
var MerchantRecognitionTokenStore = /** @class */ (function () {
    function MerchantRecognitionTokenStore(eventReporter, cookieDocument, now) {
        if (cookieDocument === void 0) { cookieDocument = document; }
        if (now === void 0) { now = Date.now; }
        this.eventReporter = eventReporter;
        this.cookieDocument = cookieDocument;
        this.now = now;
    }
    MerchantRecognitionTokenStore.prototype.read = function (network) {
        var encodedToken = this.readEncodedCookie(network);
        if (encodedToken === undefined) {
            return undefined;
        }
        var token;
        try {
            token = decodeURIComponent(encodedToken);
        }
        catch (_a) {
            this.reportStorageError(network, "TOKEN_INVALID");
            this.remove(network);
            return undefined;
        }
        if (getExpiration(token, this.now()) === undefined) {
            this.reportStorageError(network, "TOKEN_INVALID");
            this.remove(network);
            return undefined;
        }
        this.reportStorageSuccess(network, "READ");
        return token;
    };
    MerchantRecognitionTokenStore.prototype.write = function (network, token) {
        if (token === undefined || token === null || token === "") {
            this.reportStorageError(network, "TOKEN_MISSING");
            return;
        }
        if (typeof token !== "string") {
            this.reportStorageError(network, "TOKEN_INVALID");
            return;
        }
        var now = this.now();
        var tokenExpiration = getExpiration(token, now);
        if (tokenExpiration === undefined) {
            this.reportStorageError(network, "TOKEN_INVALID");
            return;
        }
        var encodedToken;
        try {
            encodedToken = encodeURIComponent(token);
        }
        catch (_a) {
            this.reportStorageError(network, "TOKEN_INVALID");
            return;
        }
        var cookieName = MerchantRecognitionTokenCookieNames[network];
        var expires = new Date(Math.min(tokenExpiration, now + MAX_COOKIE_LIFETIME_MS)).toUTCString();
        try {
            this.cookieDocument.cookie = "".concat(cookieName, "=").concat(encodedToken, "; Expires=").concat(expires, "; ").concat(COOKIE_ATTRIBUTES);
        }
        catch (_b) {
            this.reportStorageError(network, "COOKIE_WRITE_FAILED");
            return;
        }
        if (this.readEncodedCookie(network) !== encodedToken) {
            this.reportStorageError(network, "COOKIE_WRITE_FAILED");
            return;
        }
        this.reportStorageSuccess(network, "WRITE");
    };
    MerchantRecognitionTokenStore.prototype.remove = function (network) {
        if (this.readEncodedCookie(network) === undefined) {
            return;
        }
        var cookieName = MerchantRecognitionTokenCookieNames[network];
        try {
            this.cookieDocument.cookie = "".concat(cookieName, "=; Expires=").concat(DELETE_EXPIRY, "; ").concat(COOKIE_ATTRIBUTES);
        }
        catch (_a) {
            this.reportStorageError(network, "COOKIE_DELETE_FAILED");
            return;
        }
        if (this.readEncodedCookie(network) !== undefined) {
            this.reportStorageError(network, "COOKIE_DELETE_FAILED");
            return;
        }
        this.reportStorageSuccess(network, "DELETE");
    };
    MerchantRecognitionTokenStore.prototype.removeAll = function () {
        this.remove(DcfSdkEnumsObj.VISA);
        this.remove(DcfSdkEnumsObj.MASTERCARD);
    };
    MerchantRecognitionTokenStore.prototype.readEncodedCookie = function (network) {
        var cookieName = MerchantRecognitionTokenCookieNames[network];
        try {
            var cookiePrefix_1 = "".concat(cookieName, "=");
            var cookie = this.cookieDocument.cookie
                .split(";")
                .map(function (value) { return value.trim(); })
                .find(function (value) { return value.startsWith(cookiePrefix_1); });
            return cookie === null || cookie === void 0 ? void 0 : cookie.slice(cookiePrefix_1.length);
        }
        catch (_a) {
            return undefined;
        }
    };
    MerchantRecognitionTokenStore.prototype.reportStorageError = function (network, reason) {
        try {
            this.eventReporter.sendEvent(SrciEventTypeEnumsObj.RECOGNITION_TOKEN_STORAGE_ERROR, { network: network, reason: reason });
        }
        catch (_a) {
            // Storage diagnostics must not affect the checkout flow.
        }
    };
    MerchantRecognitionTokenStore.prototype.reportStorageSuccess = function (network, operation) {
        try {
            this.eventReporter.sendEvent(SrciEventTypeEnumsObj.RECOGNITION_TOKEN_STORAGE_SUCCESS, { network: network, operation: operation });
        }
        catch (_a) {
            // Storage diagnostics must not affect the checkout flow.
        }
    };
    return MerchantRecognitionTokenStore;
}());
export { MerchantRecognitionTokenStore };
